import { useEffect, useState } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Checkbox,
    Menu,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Target } from "lucide-react";
import { fetchFactFindHistory, saveFactFind } from "../services/factfind.service";
import QuillEditor from "../meeting/QuillEditor";
import { sanitizeDisplayHtml } from "../utils/sanitizeHtml";
import { diffWordsWithSpace } from "diff";

// --- Helper Functions ---

/**
 * Converts a schema displayLabel to a camelCase Mongoose key.
 * IMPORTANT: This must be IDENTICAL to the version in ClientDetails.tsx to ensure correct data lookup.
 */
const getMongooseKey = (label: string): string => {
    if (!label) return "";
    const lower = label.toLowerCase().trim();

    // --- Synced Overrides (Must match ClientDetails.tsx) ---
    if (lower === "riskprofile") return "riskprofile";
    if (lower === "employment") return "employment";

    if (lower === "annual interest rate" || lower === "annual interest rate (%)" || lower === "annual interest rate %") {
        return "annualInterestRatePercent";
    }

    if (lower === "insured person(s)" || lower === "insured person") {
        return "insuredPersons";
    }

    // Fix for Interest Rate consistency
    if (lower === "interest rate" || lower === "interest rate (%)" || lower === "interest rate %") {
        return "interestRatePercent";
    }
    // -------------------------------------------------------

    return label
        .replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) => (chr ? chr.toUpperCase() : ""))
        .replace(/^./, (str) => str.toLowerCase());
};

function formatField(key: string) {
    if (!key || typeof key !== "string") return "";
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
}
// ---


// Type for a single FactFind document from our DB
type FactFindVersion = {
    _id: string;
    clientId: { _id: string; name: string };
    version: number;
    status: 'active' | 'archived';
    fileName: string;
    factFindData: any;
    clientGraphData?: any;
    createdAt: string;
    updatedAt: string;
};

/**
 * Flattens factFindData into a simple HTML string for read-only viewing.
 * UPDATED: Forces empty sections to display field labels with "N/A".
 */
const flattenFactFindToHtml = (data: any, schema: any): string => {
    if (!data || !schema?.factfindTemplate?.categories) return "<p>No data to display.</p>";

    let html = "";
    const categories = schema.factfindTemplate.categories;

    categories.forEach((category: any) => {
        html += `<h2>${category.name}</h2>`;
        if (!category.blocks) return;

        category.blocks.forEach((block: any) => {
            const blockKey = getMongooseKey(block.section);
            const blockData = data[blockKey];

            const renderFieldsToHtml = (fields: any[], itemData: any) => {
                fields.forEach((field: any) => {
                    if (field.subsection_name) {
                        html += `<h4>${formatField(field.subsection_name)}</h4>`;
                        const subKey = getMongooseKey(field.subsection_name);
                        // If itemData is null (empty row), subItem becomes null, ensuring children render as N/A
                        const subItem = itemData ? itemData[subKey] : null;
                        renderFieldsToHtml(field.fields, subItem);
                    } else if (field.displayLabel) {
                        const fieldKey = getMongooseKey(field.displayLabel);
                        const value = itemData ? (itemData[fieldKey] || 'N/A') : 'N/A';
                        html += `<p><strong>${field.displayLabel}:</strong> ${value}</p>`;
                    }
                });
            };

            if (block.type === 'array') {
                // If array is empty, iterate over [null] to render fields at least once
                const itemsToRender = (blockData && blockData.length > 0) ? blockData : [null];

                itemsToRender.forEach((item: any, index: number) => {
                    html += `<h3>${formatField(block.section)} ${index + 1}</h3>`;
                    renderFieldsToHtml(block.fields, item);
                });
            } else if (block.type === 'object') {
                html += `<h3>${formatField(block.section)}</h3>`;
                // If blockData is missing, pass null to force N/A rendering
                renderFieldsToHtml(block.fields, blockData || null);
            }
        });
    });

    return html;
};

/**
 * Flattens factFindData into PLAIN TEXT for diffing.
 * UPDATED: Includes empty fields so diffs show when data goes from "N/A" to Value.
 */
const cleanFactFindData = (data: any, schema: any): string => {
    if (!data || !schema?.factfindTemplate?.categories) return "No data.";

    let text = "";
    const categories = schema.factfindTemplate.categories;

    categories.forEach((category: any) => {
        text += `${category.name}\n====================\n`;
        if (!category.blocks) return;

        category.blocks.forEach((block: any) => {
            const blockKey = getMongooseKey(block.section);
            const blockData = data[blockKey];

            const cleanFields = (fields: any[], itemData: any) => {
                fields.forEach((field: any) => {
                    if (field.subsection_name) {
                        text += `\n  ${formatField(field.subsection_name)}:\n`;
                        const subKey = getMongooseKey(field.subsection_name);
                        cleanFields(field.fields, itemData ? itemData[subKey] : null);
                    } else if (field.displayLabel) {
                        const fieldKey = getMongooseKey(field.displayLabel);
                        const value = itemData ? (itemData[fieldKey] || 'N/A') : 'N/A';
                        text += `  ${field.displayLabel}: ${value}\n`;
                    }
                });
            };

            if (block.type === 'array') {
                //  Render at least one block of "N/A" if empty
                const itemsToRender = (blockData && blockData.length > 0) ? blockData : [null];

                itemsToRender.forEach((item: any, index: number) => {
                    text += `\n${formatField(block.section)} ${index + 1}\n--------------------\n`;
                    cleanFields(block.fields, item);
                });
            } else if (block.type === 'object') {
                text += `\n${formatField(block.section)}\n--------------------\n`;
                cleanFields(block.fields, blockData || null);
            }
        });
        text += "\n";
    });
    return text.replace(/\n\s*\n/g, "\n").trim();
};

// --- Type for compare state ---
type CompareItem = {
    text: string;
    status: 'active' | 'archived';
    timestamp: string;
}

export const FactFindVersionHistoryDrawer = ({
    open,
    onClose,
    clientId,
    schema,
    onRestoreVersion,
}: {
    open: boolean;
    onClose: () => void;
    clientId: string | null;
    schema: any;
    onRestoreVersion: (newVersion: any) => void;
}) => {
    const [versions, setVersions] = useState<FactFindVersion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedVersionIdxs, setSelectedVersionIdxs] = useState<number[]>([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuVersionIdx, setMenuVersionIdx] = useState<number | null>(null);

    const [showFileDialogOpen, setShowFileDialogOpen] = useState(false);
    const [versionToShow, setVersionToShow] = useState<FactFindVersion | null>(null);

    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [versionToRestore, setVersionToRestore] = useState<FactFindVersion | null>(null);
    const [isRestoring, setIsRestoring] = useState(false);

    // State for Compare Dialog
    const [compareDialogOpen, setCompareDialogOpen] = useState(false);
    const [compareContent, setCompareContent] = useState<null | { old: CompareItem, new: CompareItem }>(null);
    const [highlightChanges, setHighlightChanges] = useState(true);

    useEffect(() => {
        if (open && clientId) {
            const fetchVersions = async () => {
                setLoading(true);
                setError(null);
                try {
                    const data = await fetchFactFindHistory(clientId);
                    setVersions(data || []);
                } catch (e: any) {
                    setError(e.message || "Failed to fetch version history.");
                } finally {
                    setLoading(false);
                }
            };
            fetchVersions();
        }
    }, [open, clientId]);

    const handleMenuOpen = (event: any, index: number) => {
        setAnchorEl(event.currentTarget);
        setMenuVersionIdx(index);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuVersionIdx(null);
    };

    const handleShowFile = (version: FactFindVersion) => {
        handleMenuClose();
        setVersionToShow(version);
        setShowFileDialogOpen(true);
    };

    const handleRestoreVersion = (version: FactFindVersion) => {
        handleMenuClose();
        setVersionToRestore(version);
        setRestoreDialogOpen(true);
    };

    const confirmRestoreVersion = async () => {
        if (!versionToRestore || !clientId) return;

        setIsRestoring(true);
        try {
            const newActiveVersion = await saveFactFind(clientId, {
                factFindData: versionToRestore.factFindData,
                clientGraphData: versionToRestore.clientGraphData ?? null,
            });

            onRestoreVersion(newActiveVersion);
            setRestoreDialogOpen(false);
            setVersionToRestore(null);
            onClose();
        } catch (e) {
            console.error("Failed to restore version", e);
        } finally {
            setIsRestoring(false);
        }
    };

    const handleCheckboxChange = (idx: number) => {
        setSelectedVersionIdxs((prev) => {
            if (prev.includes(idx)) {
                return prev.filter((i) => i !== idx);
            } else {
                const newSelection = [...prev, idx];
                return newSelection.length > 2 ? [prev[1], idx] : newSelection;
            }
        });
    };

    const handleCompareVersion = () => {
        const [firstIdx, secondIdx] = selectedVersionIdxs;
        const versionA = versions[firstIdx];
        const versionB = versions[secondIdx];

        const formatDate = (dateString: string) =>
            new Date(dateString).toLocaleString("en-US", {
                weekday: "short",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
            });

        if (versionA && versionB) {

            const oldVersion = versionA.version < versionB.version ? versionA : versionB;
            const newVersion = versionA.version > versionB.version ? versionA : versionB;

            setCompareContent({
                old: {
                    text: cleanFactFindData(oldVersion.factFindData, schema),
                    status: oldVersion.status,
                    timestamp: formatDate(oldVersion.createdAt)
                },
                new: {
                    text: cleanFactFindData(newVersion.factFindData, schema),
                    status: newVersion.status,
                    timestamp: formatDate(newVersion.createdAt)
                },
            });

            setCompareDialogOpen(true);
        }
    };

    let dynamicFileName = "Fact Find";
    if (!loading && versions.length > 0) {
        const activeVersion = versions[0];
        if (activeVersion.fileName) {
            dynamicFileName = activeVersion.fileName;
        }
    }

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: "25vw",
                        height: "calc(100vh - 65px)",
                        mt: "62px",
                        bgcolor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        px: 3,
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 0,
                        pt: 3,
                        pb: 2,
                        borderBottom: "1px solid #eee",
                    }}
                >
                    <Typography variant="h6" fontWeight={600}>
                        Version History
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Dynamic File Name */}
                <Box sx={{ pt: 2, pb: 1 }}>
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#222",
                            mb: 1,
                        }}
                    >
                        {loading ? <Skeleton width="60%" /> : dynamicFileName}
                    </Typography>
                </Box>

                {/* Version List */}
                <Box sx={{ flex: 1, ml: 0, overflowY: "auto", mt: 1 }}>
                    {loading && (
                        <Box sx={{ pt: 2 }}>
                            <Skeleton variant="text" width="80%" height={40} />
                            <Skeleton variant="text" width="50%" height={20} />
                            <Skeleton variant="rectangular" width="100%" height={30} sx={{ mt: 1 }} />
                            <Skeleton variant="text" width="80%" height={40} sx={{ mt: 2 }} />
                            <Skeleton variant="text" width="50%" height={20} />
                        </Box>
                    )}
                    {!loading && error && (
                        <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
                    )}
                    {!loading && !error && versions.map((v, idx) => (
                        <Box
                            key={v._id}
                            sx={{ mb: 2, marginLeft: 0 }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            mr: 1,
                                        }}
                                    >
                                        <Target
                                            color={v.status === 'active' ? "#1976d2" : "#9e9e9e"}
                                            size={18}
                                        />
                                        {idx < versions.length - 1 && (
                                            <Box
                                                sx={{
                                                    width: "10%",
                                                    height: 28,
                                                    bgcolor: "#d0d7de",
                                                    mt: 0.5,
                                                    borderRadius: 1,
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography variant="body1" fontWeight={700}>
                                            Version {v.version}
                                            {v.status === 'active' && <span style={{ color: '#1976d2' }}> (Active)</span>}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {new Date(v.createdAt).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
                                    <Checkbox
                                        sx={{ ml: 1 }}
                                        checked={selectedVersionIdxs.includes(idx)}
                                        onChange={() => handleCheckboxChange(idx)}
                                        disabled={
                                            selectedVersionIdxs.length === 2 &&
                                            !selectedVersionIdxs.includes(idx)
                                        }
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, idx)}
                                    >
                                        <MoreVertIcon fontSize="small" sx={{ color: "#000" }} />
                                    </IconButton>

                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl) && menuVersionIdx === idx}
                                        onClose={handleMenuClose}
                                    >
                                        <MenuItem onClick={() => handleShowFile(v)}>
                                            Show file
                                        </MenuItem>
                                        <MenuItem onClick={() => handleRestoreVersion(v)} disabled={v.status === 'active'}>
                                            Restore version
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Footer Buttons */}
                <Box
                    sx={{
                        py: 4,
                        borderTop: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-evenly",
                        gap: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ flex: 1 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={selectedVersionIdxs.length !== 2}
                        onClick={handleCompareVersion}
                        sx={{ flex: 1 }}
                    >
                        Compare Versions
                    </Button>
                </Box>
            </Drawer>

            {/* Restore Dialog */}
            <Dialog
                open={restoreDialogOpen}
                onClose={() => setRestoreDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Restore Version</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to restore this version? This will create a new
                        active version based on this one, and archive your current version.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestoreDialogOpen(false)} color="primary" disabled={isRestoring}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRestoreVersion}
                        color="primary"
                        variant="contained"
                        disabled={isRestoring}
                    >
                        {isRestoring ? <CircularProgress size={24} /> : "Yes, Restore"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Show File Dialog */}
            <Dialog
                open={showFileDialogOpen}
                onClose={() => setShowFileDialogOpen(false)}
                maxWidth="md"
                fullWidth
                sx={{
                    "& .MuiDialog-paper": {
                        height: "80vh",
                    },
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Viewing Version {versionToShow?.version}
                    <IconButton onClick={() => setShowFileDialogOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, height: '100%' }}>
                    <QuillEditor
                        editorContent={versionToShow ? flattenFactFindToHtml(versionToShow.factFindData, schema) : ""}
                        setEditorContent={() => { }} // Read-only
                        readOnly={true}
                        height="100%"
                    />
                </DialogContent>
            </Dialog>

            {/* Compare Dialog */}
            <Dialog
                open={compareDialogOpen}
                onClose={() => {
                    setCompareDialogOpen(false);
                    setSelectedVersionIdxs([]);
                    setCompareContent(null); // Clear content
                }}
                maxWidth="md"
                fullWidth
                sx={{
                    p: 0,
                    "& .MuiDialog-paper": {
                        height: "80vh",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingY: 2,
                        paddingX: 4,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontSize={16}
                        sx={{ fontWeight: 600 }}
                    >
                        Compare versions
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Checkbox
                                checked={highlightChanges}
                                onChange={(e) => setHighlightChanges(e.target.checked)}
                                size="small"
                            />
                            <Typography variant="body2" color={"text.secondary"}>
                                Highlight changes
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => {
                                setCompareDialogOpen(false);
                                setSelectedVersionIdxs([]);
                                setCompareContent(null); // Clear content
                            }}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        py: 2,
                        display: 'flex',
                        gap: 2,
                        overflowY: "auto",
                        height: "100%",
                    }}
                >
                    {/* This renders only when compareContent is set */}
                    {compareContent && (() => {
                        let oldContentHtml = compareContent.old.text;
                        let newContentHtml = compareContent.new.text;

                        if (highlightChanges) {
                            const diff = diffWordsWithSpace(
                                compareContent.old.text,
                                compareContent.new.text
                            );

                            oldContentHtml = diff
                                .map((part) => {
                                    if (part.removed) {
                                        return `<span style="background-color:#ffcdd2; color:#d32f2f; text-decoration:line-through;">${part.value}</span>`;
                                    } else if (!part.added) {
                                        return part.value;
                                    }
                                    return "";
                                })
                                .join("");

                            newContentHtml = diff
                                .map((part) => {
                                    if (part.added) {
                                        return `<span style="background-color:#c8e6c9; color:#2e7d32; font-weight:bold;">${part.value}</span>`;
                                    } else if (!part.removed) {
                                        return part.value;
                                    }
                                    return "";
                                })
                                .join("");
                        }

                        oldContentHtml = sanitizeDisplayHtml(oldContentHtml);
                        newContentHtml = sanitizeDisplayHtml(newContentHtml);

                        return (
                            <>
                                {/* Old Version Box */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        border: "1px solid #eee",
                                        borderRadius: 2,
                                        background: "#F6F6F6",
                                        overflow: "hidden",
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 2,
                                            background: "#EAEAEA",
                                            borderBottom: "1px solid #eee",
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Target
                                            color={compareContent.old.status === 'active' ? "#1976d2" : "#9e9e9e"}
                                            size={16}
                                        />
                                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                            {compareContent.old.timestamp}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "inherit",
                                                lineHeight: "1.6",
                                            }}
                                            dangerouslySetInnerHTML={{ __html: oldContentHtml }}
                                        />
                                    </Box>
                                </Box>
                                {/* New Version Box */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        border: "1px solid #eee",
                                        borderRadius: 2,
                                        background: "#F6F6F6",
                                        overflow: "hidden",
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 2,
                                            background: "#EAEAEA",
                                            borderBottom: "1px solid #eee",
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Target
                                            color={compareContent.new.status === 'active' ? "#1976d2" : "#9e9e9e"}
                                            size={16}
                                        />
                                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                            {compareContent.new.timestamp}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "inherit",
                                                lineHeight: "1.6",
                                            }}
                                            dangerouslySetInnerHTML={{ __html: newContentHtml }}
                                        />
                                    </Box>
                                </Box>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </>
    );
};